/**
 * Overview Panel Component
 * Displays aggregate pipeline statistics with warning indicators
 */

import type { OverviewStats } from '@tender-monitor/shared/types/admin';
import { formatRelativeTime } from '../../utils/formatTime';

type OverviewPanelProps = {
  data: OverviewStats;
};

export function OverviewPanel({ data }: OverviewPanelProps) {
  const totalTenderLinks =
    data.tenderLinks.pending +
    data.tenderLinks.processing +
    data.tenderLinks.completed +
    data.tenderLinks.failed +
    data.tenderLinks.skipped;

  const totalGeolocation =
    data.geolocation.pending +
    data.geolocation.success +
    data.geolocation.failed +
    data.geolocation.notFound;

  return (
    <div className="overview-panel-container">
      {/* Stale Data Warning */}
      {data.isStale && (
        <div className="stale-warning">
          <span className="stale-warning__icon">⚠️</span>
          <span className="stale-warning__text">
            Dane mogą być nieaktualne. Ostatnie pobranie:{' '}
            {data.lastCrawlAt ? formatRelativeTime(data.lastCrawlAt) : 'nigdy'}
          </span>
        </div>
      )}

      {/* Source Errors Warning */}
      {data.sources.withErrors > 0 && (
        <div className="stale-warning">
          <span className="stale-warning__icon">🔴</span>
          <span className="stale-warning__text">
            {data.sources.withErrors} źródeł z błędami wymaga uwagi
          </span>
        </div>
      )}

      <div className="overview-panel">
        {/* Sources */}
        <div className="overview-card">
          <p className="overview-card__title">Aktywne źródła</p>
          <p className="overview-card__value">{data.sources.active}</p>
          <p className="overview-card__detail">
            {data.sources.inactive} nieaktywnych
          </p>
        </div>

        <div className={`overview-card ${data.sources.withErrors > 0 ? 'overview-card--error' : ''}`}>
          <p className="overview-card__title">Źródła z błędami</p>
          <p className="overview-card__value">{data.sources.withErrors}</p>
          <p className="overview-card__detail">
            wymaga sprawdzenia
          </p>
        </div>

        {/* Tender Links */}
        <div className={`overview-card ${data.tenderLinks.pending > 0 ? 'overview-card--warning' : ''}`}>
          <p className="overview-card__title">Linki oczekujące</p>
          <p className="overview-card__value">{data.tenderLinks.pending}</p>
          <p className="overview-card__detail">
            {data.tenderLinks.processing} w trakcie
          </p>
        </div>

        <div className="overview-card overview-card--success">
          <p className="overview-card__title">Linki przetworzone</p>
          <p className="overview-card__value">{data.tenderLinks.completed}</p>
          <p className="overview-card__detail">
            z {totalTenderLinks} (ostatnie 7 dni)
          </p>
        </div>

        <div className={`overview-card ${data.tenderLinks.failed > 0 ? 'overview-card--error' : ''}`}>
          <p className="overview-card__title">Linki z błędami</p>
          <p className="overview-card__value">{data.tenderLinks.failed}</p>
          <p className="overview-card__detail">
            {data.tenderLinks.skipped} pominiętych
          </p>
        </div>

        {/* Geolocation */}
        <div className={`overview-card ${data.geolocation.pending > 0 ? 'overview-card--warning' : ''}`}>
          <p className="overview-card__title">Geolokalizacja oczekuje</p>
          <p className="overview-card__value">{data.geolocation.pending}</p>
          <p className="overview-card__detail">
            działek do przetworzenia
          </p>
        </div>

        <div className="overview-card overview-card--success">
          <p className="overview-card__title">Geolokalizacja OK</p>
          <p className="overview-card__value">{data.geolocation.success}</p>
          <p className="overview-card__detail">
            z {totalGeolocation} (ostatnie 7 dni)
          </p>
        </div>

        <div className={`overview-card ${data.geolocation.failed > 0 ? 'overview-card--error' : ''}`}>
          <p className="overview-card__title">Geolokalizacja błędy</p>
          <p className="overview-card__value">{data.geolocation.failed}</p>
          <p className="overview-card__detail">
            {data.geolocation.notFound} nie znaleziono
          </p>
        </div>

        {/* Last Crawl */}
        <div className="overview-card">
          <p className="overview-card__title">Ostatnie pobranie</p>
          <p className="overview-card__value" style={{ fontSize: '1rem' }}>
            {data.lastCrawlAt ? formatRelativeTime(data.lastCrawlAt) : 'Nigdy'}
          </p>
          <p className="overview-card__detail">
            {data.lastCrawlAt ? new Date(data.lastCrawlAt).toLocaleString('pl-PL') : ''}
          </p>
        </div>
      </div>
    </div>
  );
}
